import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Download, FileText, Film, Play, Pause, ChevronLeft,
  Presentation, Volume2, Copy, Check, ChevronDown, ChevronUp,
  Mic, Loader2, AlertCircle
} from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;

const AD_CLIPS = [
  { file: 'clip1_hero.mp4', title: 'Care Facility Aerial', desc: 'Cinematic aerial flyover of a modern care facility at golden hour' },
  { file: 'clip2_tech.mp4', title: 'Smart Scheduling UI', desc: 'Close-up of scheduling dashboard with shift assignment workflow' },
  { file: 'clip3_caregiver.mp4', title: 'Caregiver in Action', desc: 'Professional caregiver assisting client with mobile notifications' },
  { file: 'clip4_family.mp4', title: 'Family Portal', desc: 'Family viewing real-time care reports on tablet' },
  { file: 'clip5_data.mp4', title: 'Data & Security', desc: 'Analytics visualization with secure data indicators' },
];

const TUTORIAL_CLIPS = [
  { file: 'tut1_dashboard.mp4', title: 'Dashboard Overview', desc: 'Main dashboard with stats, shifts, and compliance at a glance' },
  { file: 'tut2_scheduling.mp4', title: 'Shift Scheduling', desc: 'Creating and assigning shifts with AI Smart Suggest' },
  { file: 'tut3_messaging.mp4', title: 'Messaging & Rooms', desc: 'End-to-end encrypted messaging with file sharing' },
  { file: 'tut4_mobile.mp4', title: 'Mobile App', desc: 'Full-featured mobile app with offline support' },
];

const AD_VOICEOVER = `Managing a care agency should not mean drowning in paperwork, juggling spreadsheets, and playing phone tag with caregivers.

Introducing FendanaCura \u2014 the all-in-one platform that transforms how care agencies operate.

With AI-powered Smart Scheduling, we match the right caregiver to every shift \u2014 considering availability, proximity, skills, and client familiarity. One click. Done.

Track your entire team in real time with GPS clock-in and clock-out. Get instant no-show alerts. See your operations on a live heatmap. Never miss a compliance deadline again.

FendanaCura automatically tracks certifications, DBS checks, and training renewals \u2014 sending alerts before anything expires.

Stay connected with end-to-end encrypted messaging. Group chat rooms for teams. File sharing. Typing indicators. All secure, all in one place.

And for families? Our dedicated Family Portal provides real-time care reports, medication logs, and activity updates \u2014 building the trust that matters most.

With support for twelve currencies, live exchange rates, and one-click invoice PDF generation, billing has never been easier.

FendanaCura works everywhere \u2014 on the web, on your phone, even offline. Your data syncs automatically when you reconnect.

Start your free 30-day trial today. No credit card required. FendanaCura \u2014 Smart care, seamlessly managed.`;

const TUTORIAL_VOICEOVER = `Welcome to FendanaCura. Let me walk you through the key features of your care management platform.

When you first log in, you will land on your dashboard. Here you can see a quick overview of today's shifts, pending tasks, team notifications, and compliance status \u2014 all at a glance.

To manage shifts, navigate to the Scheduling section. You can create shifts by clicking the plus button, then fill in the client, date, time, and care type. Use Smart Suggest to let our AI recommend the best caregiver based on availability, proximity, and skills. One click to assign. It is that simple.

For messaging, tap the Messages tab. You will see your direct conversations and can access Group Rooms by tapping the Rooms button. All messages are end-to-end encrypted. You can share files by tapping the attachment button, and you will see real-time typing indicators when someone is responding.

The Compliance section tracks all caregiver documents. You will get automatic alerts before any certification expires. Green means compliant, red means action needed.

For billing, go to the Subscription dashboard. You can change your currency, view your plan, and download invoices as PDF \u2014 all in your local currency.

On mobile, download the FendanaCura app for full access on the go. The app works offline too. Any data you enter will sync automatically when you are back online.

That is FendanaCura in two minutes. Ready to get started? Head to your dashboard now.`;

function VideoCard({ clip, index }) {
  const [playing, setPlaying] = useState(false);
  const videoUrl = `${BASE}/assets/videos/${clip.file}`;

  const togglePlay = (e) => {
    const video = e.currentTarget.closest('.video-card-wrapper').querySelector('video');
    if (video) {
      if (playing) { video.pause(); } else { video.play(); }
      setPlaying(!playing);
    }
  };

  return (
    <div className="video-card-wrapper group">
      <Card className="glass-card overflow-hidden">
        <div className="relative aspect-video bg-black/40">
          <video
            className="w-full h-full object-cover"
            src={videoUrl}
            preload="metadata"
            playsInline
            onEnded={() => setPlaying(false)}
            onPause={() => setPlaying(false)}
            onPlay={() => setPlaying(true)}
            data-testid={`video-${clip.file}`}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-100 group-hover:opacity-100 transition-opacity">
            <button
              onClick={togglePlay}
              className="w-12 h-12 rounded-full bg-[#FF6F61]/90 hover:bg-[#FF6F61] flex items-center justify-center transition-transform hover:scale-110"
              data-testid={`play-btn-${clip.file}`}
            >
              {playing ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
            </button>
          </div>
          <span className="absolute top-2 left-2 text-xs bg-black/60 text-white/80 px-2 py-0.5 rounded font-mono">
            Clip {index + 1}
          </span>
        </div>
        <CardContent className="p-3">
          <h4 className="text-sm font-semibold text-foreground">{clip.title}</h4>
          <p className="text-xs text-muted-foreground mt-0.5">{clip.desc}</p>
        </CardContent>
      </Card>
    </div>
  );
}

function ScriptPanel({ title, script, testId }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(script);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const lines = script.split('\n');
  const preview = lines.slice(0, 6).join('\n');

  return (
    <Card className="glass-card" data-testid={testId}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-foreground flex items-center gap-2">
            <Volume2 className="w-4 h-4 text-[#FF6F61]" />
            {title}
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={handleCopy} data-testid={`copy-script-${testId}`}>
              {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
              <span className="ml-1 text-xs">{copied ? 'Copied' : 'Copy'}</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              <span className="ml-1 text-xs">{expanded ? 'Collapse' : 'Expand'}</span>
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="bg-black/30 rounded-lg p-4 border border-border/30">
          <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-sans leading-relaxed">
            {expanded ? script : preview + '\n...'}
          </pre>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2 italic">
          Use this script to record your own voiceover, or paste into any text-to-speech service.
        </p>
      </CardContent>
    </Card>
  );
}

const VOICE_OPTIONS = [
  { id: 'alloy', name: 'Alloy', desc: 'Neutral, balanced' },
  { id: 'echo', name: 'Echo', desc: 'Warm, conversational' },
  { id: 'fable', name: 'Fable', desc: 'Expressive, storytelling' },
  { id: 'onyx', name: 'Onyx', desc: 'Deep, authoritative' },
  { id: 'nova', name: 'Nova', desc: 'Bright, energetic' },
  { id: 'shimmer', name: 'Shimmer', desc: 'Clear, gentle' },
];

function VoiceoverGenerator() {
  const [text, setText] = useState('');
  const [voice, setVoice] = useState('nova');
  const [model, setModel] = useState('tts-1-hd');
  const [speed, setSpeed] = useState(0.95);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const charCount = text.length;
  const estimatedDuration = Math.round((charCount / 15) / speed);

  const handleGenerate = async () => {
    if (!text.trim()) return;
    setGenerating(true);
    setError('');
    setResult(null);

    try {
      const { data } = await axios.post(`${BASE}/api/tts/generate`, {
        text: text.trim(),
        voice,
        model,
        speed,
      }, { withCredentials: true });

      setResult({
        url: `${BASE}${data.audio_url}`,
        size: data.size_bytes,
        voice: data.voice,
        fileId: data.file_id,
      });
      toast.success('Voiceover generated successfully');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Generation failed';
      setError(detail);
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  const useAdScript = () => setText(AD_VOICEOVER);
  const useTutorialScript = () => setText(TUTORIAL_VOICEOVER);

  return (
    <Card className="glass-card border-[#00A6A0]/20" data-testid="voiceover-generator">
      <CardHeader>
        <CardTitle className="text-base text-foreground flex items-center gap-2">
          <Mic className="w-5 h-5 text-[#00A6A0]" />
          Generate Voiceover
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quick-fill buttons */}
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={useAdScript}
            className="text-xs border-[#FF6F61]/30 hover:bg-[#FF6F61]/10"
            data-testid="use-ad-script-btn">
            Use Ad Script
          </Button>
          <Button variant="outline" size="sm" onClick={useTutorialScript}
            className="text-xs border-[#00A6A0]/30 hover:bg-[#00A6A0]/10"
            data-testid="use-tutorial-script-btn">
            Use Tutorial Script
          </Button>
        </div>

        {/* Text input */}
        <div>
          <textarea
            value={text}
            onChange={e => setText(e.target.value.slice(0, 5000))}
            placeholder="Paste or type your voiceover script here..."
            className="w-full rounded-lg bg-black/20 border border-border/50 text-foreground p-3 text-sm min-h-[120px] resize-y focus:border-[#00A6A0]/50 focus:outline-none"
            data-testid="voiceover-text-input"
          />
          <div className="flex justify-between mt-1">
            <p className="text-[10px] text-muted-foreground">
              ~{estimatedDuration}s estimated duration at {speed}x speed
            </p>
            <p className={`text-[10px] ${charCount > 4500 ? 'text-red-400' : 'text-muted-foreground'}`}>
              {charCount}/5000
            </p>
          </div>
        </div>

        {/* Controls row */}
        <div className="grid sm:grid-cols-3 gap-3">
          {/* Voice selector */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Voice</label>
            <select
              value={voice}
              onChange={e => setVoice(e.target.value)}
              className="w-full rounded-lg bg-black/20 border border-border/50 text-foreground p-2 text-sm focus:border-[#00A6A0]/50 focus:outline-none"
              data-testid="voice-select"
            >
              {VOICE_OPTIONS.map(v => (
                <option key={v.id} value={v.id}>{v.name} — {v.desc}</option>
              ))}
            </select>
          </div>

          {/* Model selector */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Quality</label>
            <select
              value={model}
              onChange={e => setModel(e.target.value)}
              className="w-full rounded-lg bg-black/20 border border-border/50 text-foreground p-2 text-sm focus:border-[#00A6A0]/50 focus:outline-none"
              data-testid="model-select"
            >
              <option value="tts-1">Standard (faster)</option>
              <option value="tts-1-hd">HD (higher quality)</option>
            </select>
          </div>

          {/* Speed slider */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Speed: {speed}x</label>
            <input
              type="range"
              min="0.5"
              max="2.0"
              step="0.05"
              value={speed}
              onChange={e => setSpeed(parseFloat(e.target.value))}
              className="w-full accent-[#00A6A0] mt-1"
              data-testid="speed-slider"
            />
          </div>
        </div>

        {/* Generate button */}
        <Button
          className="w-full bg-[#00A6A0] hover:bg-[#008A85] text-white"
          onClick={handleGenerate}
          disabled={generating || !text.trim()}
          data-testid="generate-voiceover-btn"
        >
          {generating ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
          ) : (
            <><Mic className="w-4 h-4 mr-2" />Generate Voiceover</>
          )}
        </Button>

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="tts-error">
            <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
            <p className="text-xs text-red-400">{error}</p>
          </div>
        )}

        {/* Result audio player */}
        {result && (
          <div className="p-4 rounded-lg bg-[#00A6A0]/5 border border-[#00A6A0]/20 space-y-3" data-testid="tts-result">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Volume2 className="w-4 h-4 text-[#00A6A0]" />
                <span className="text-sm text-foreground font-medium">Generated Audio</span>
              </div>
              <span className="text-[10px] text-muted-foreground">
                {(result.size / 1024).toFixed(0)} KB · {result.voice}
              </span>
            </div>
            <audio controls className="w-full" src={result.url} data-testid="tts-audio-player" />
            <Button
              variant="outline" size="sm"
              className="border-[#00A6A0]/30"
              onClick={() => {
                const a = document.createElement('a');
                a.href = result.url;
                a.download = `voiceover_${result.fileId}.mp3`;
                a.click();
              }}
              data-testid="download-voiceover-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />Download MP3
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function MarketingAssets() {
  const navigate = useNavigate();
  const pptUrl = `${BASE}/assets/FendanaCura_Presentation.pptx`;

  const downloadFile = (url, filename) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 max-w-6xl mx-auto" data-testid="marketing-assets-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2 -ml-2" data-testid="back-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              Marketing Assets
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Presentation, ad video clips, and tutorial video clips for FendanaCura
            </p>
          </div>
        </div>

        {/* Full Stitched Videos */}
        <div className="grid md:grid-cols-2 gap-6" data-testid="full-videos-section">
          <Card className="glass-card border-[#FF6F61]/20 overflow-hidden">
            <div className="relative aspect-video bg-black">
              <video
                className="w-full h-full object-contain"
                src={`${BASE}/assets/videos/FendanaCura_Ad.mp4`}
                controls
                preload="metadata"
                playsInline
                data-testid="full-ad-video"
              />
            </div>
            <CardContent className="p-4">
              <h3 className="text-base font-heading font-semibold text-foreground">Ad Video</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Full stitched promotional video (5 clips combined)</p>
            </CardContent>
          </Card>

          <Card className="glass-card border-[#00A6A0]/20 overflow-hidden">
            <div className="relative aspect-video bg-black">
              <video
                className="w-full h-full object-contain"
                src={`${BASE}/assets/videos/FendanaCura_Tutorial.mp4`}
                controls
                preload="metadata"
                playsInline
                data-testid="full-tutorial-video"
              />
            </div>
            <CardContent className="p-4">
              <h3 className="text-base font-heading font-semibold text-foreground">Tutorial Video</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Full stitched walkthrough video (4 clips combined)</p>
            </CardContent>
          </Card>
        </div>

        {/* PPT Download */}
        <Card className="glass-card border-[#FF6F61]/20" data-testid="ppt-section">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#FF6F61]/10 flex items-center justify-center">
                  <Presentation className="w-6 h-6 text-[#FF6F61]" />
                </div>
                <div>
                  <h2 className="text-lg font-heading font-semibold text-foreground">
                    FendanaCura Presentation
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    11-slide PowerPoint deck — platform overview, features, pricing, testimonials
                  </p>
                </div>
              </div>
              <Button
                className="bg-[#FF6F61] hover:bg-[#e55a4d] text-white"
                onClick={() => downloadFile(pptUrl, 'FendanaCura_Presentation.pptx')}
                data-testid="download-ppt-btn"
              >
                <Download className="w-4 h-4 mr-2" /> Download .pptx
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Ad Video Section */}
        <section data-testid="ad-video-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#FF6F61]/10 flex items-center justify-center">
              <Film className="w-4 h-4 text-[#FF6F61]" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-semibold text-foreground">
                Ad Video Clips
              </h2>
              <p className="text-xs text-muted-foreground">
                5 cinematic clips (~4s each) for a 2-minute promotional video
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {AD_CLIPS.map((clip, i) => (
              <VideoCard key={clip.file} clip={clip} index={i} />
            ))}
          </div>

          <ScriptPanel
            title="Ad Voiceover Script (~2 min)"
            script={AD_VOICEOVER}
            testId="ad-voiceover-script"
          />
        </section>

        {/* Tutorial Video Section */}
        <section data-testid="tutorial-video-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#00A6A0]/10 flex items-center justify-center">
              <FileText className="w-4 h-4 text-[#00A6A0]" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-semibold text-foreground">
                Tutorial Video Clips
              </h2>
              <p className="text-xs text-muted-foreground">
                4 walkthrough clips (~4s each) for a 2-minute tutorial video
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            {TUTORIAL_CLIPS.map((clip, i) => (
              <VideoCard key={clip.file} clip={clip} index={i} />
            ))}
          </div>

          <ScriptPanel
            title="Tutorial Voiceover Script (~2 min)"
            script={TUTORIAL_VOICEOVER}
            testId="tutorial-voiceover-script"
          />
        </section>

        {/* Voiceover Generator */}
        <section data-testid="voiceover-generator-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#00A6A0]/10 flex items-center justify-center">
              <Mic className="w-4 h-4 text-[#00A6A0]" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-semibold text-foreground">
                Voiceover Studio
              </h2>
              <p className="text-xs text-muted-foreground">
                Generate AI voiceovers from text using OpenAI TTS
              </p>
            </div>
          </div>
          <VoiceoverGenerator />
        </section>

        {/* Usage Instructions */}
        <Card className="glass-card" data-testid="usage-instructions">
          <CardHeader>
            <CardTitle className="text-sm text-foreground">Adding Voiceover to Your Videos</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="text-xs text-muted-foreground space-y-2 list-decimal list-inside">
              <li>Download the full stitched video by right-clicking and selecting "Save video as..."</li>
              <li>Copy the voiceover script and record it using any text-to-speech tool or your own voice</li>
              <li>Import the video and audio into an editor (e.g., CapCut, DaVinci Resolve, iMovie)</li>
              <li>Overlay the voiceover audio track onto the video and export as a single MP4</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
